import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import subscriptionService, { Plan, Subscription } from '@/services/subscription';
import SubscriptionModal from '@/components/SubscriptionModal';
import { parseSubscriptionIntent, type SubscriptionRedirectContext } from '@/utils/subscriptionRedirect';
import './Subscriptions.css';

const Subscriptions: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [mySubscriptions, setMySubscriptions] = useState<Subscription[]>([]);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [localTrialUsed, setLocalTrialUsed] = useState(false);
  const [subscriptionsLoaded, setSubscriptionsLoaded] = useState(!isAuthenticated);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [cancelingSubscription, setCancelingSubscription] = useState<number | null>(null);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const intent = useMemo<SubscriptionRedirectContext>(() => parseSubscriptionIntent(location.search), [location.search]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const planSectionRef = useRef<HTMLDivElement | null>(null);
  const hasHotIntent = intent.from === 'video';

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      setSubscriptionsLoaded(false);
      fetchMySubscriptions();
    } else {
      // Effacer les abonnements si l'utilisateur n'est pas connecté
      setMySubscriptions([]);
      setLoadingSubscriptions(false);
      setHasUsedTrial(false);
      setSubscriptionsLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!hasHotIntent) {
      setShowReminderModal(false);
      return;
    }

    const reminderTimer = window.setTimeout(() => setShowReminderModal(true), 15000);
    return () => {
      window.clearTimeout(reminderTimer);
    };
  }, [hasHotIntent]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('subscription-redirecting');
    };
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await subscriptionService.getPlans();
      setPlans(data);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Impossible de charger les plans d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubscriptions = async (showLoader: boolean = false) => {
    if (!isAuthenticated) {
      return;
    }

    if (showLoader) {
      setLoadingSubscriptions(true);
    }
    try {
      const response = await subscriptionService.getMySubscriptions();
      setMySubscriptions(response.subscriptions);
      const usedTrial = Boolean(response.hasUsedTrial);
      setHasUsedTrial(usedTrial);
      let nextLocalTrialUsed = usedTrial;
      if (typeof window !== 'undefined') {
        if (usedTrial) {
          window.localStorage?.setItem('edenplaisir_has_trial', 'true');
        } else {
          window.localStorage?.removeItem('edenplaisir_has_trial');
        }
        nextLocalTrialUsed = window.localStorage?.getItem('edenplaisir_has_trial') === 'true';
      }
      setLocalTrialUsed(nextLocalTrialUsed);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      if (showLoader) {
        setLoadingSubscriptions(false);
      }
      setSubscriptionsLoaded(true);
    }
  };

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/subscriptions' } });
      return;
    }

    setProcessingPlan(planId);
    setError(null);
    document.body.classList.add('subscription-redirecting');
    setShowReminderModal(false);

    try {
      await subscriptionService.redirectToCheckout(planId);
      // Le redirect va se faire, donc pas besoin de setProcessingPlan(null) ici
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Impossible de créer la session de paiement. Veuillez réessayer.');
      setProcessingPlan(null);
      document.body.classList.remove('subscription-redirecting');
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setLocalTrialUsed(window.localStorage?.getItem('edenplaisir_has_trial') === 'true');
    }
  }, []);

  const handleCancelSubscription = async (subscription: Subscription) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler cet abonnement ?')) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    setCancelingSubscription(Number(subscription.id));

    try {
      // Utiliser l'ID de Subscription (numérique) pour l'annulation
      await subscriptionService.cancelSubscription(Number(subscription.id));
      
      // Rafraîchir la liste des abonnements pour mettre à jour l'affichage
      await fetchMySubscriptions(true);
      alert('Abonnement annulé avec succès');
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert('Impossible d\'annuler l\'abonnement. Veuillez réessayer.');
    } finally {
      setCancelingSubscription(null);
    }
  };

  const scrollToPlans = () => {
    planSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getPlanBadgeClass = (planId: string): string => {
    switch (planId) {
      case 'basic':
        return 'plan-badge-basic';
      case 'premium':
        return 'plan-badge-premium';
      case 'vip':
        return 'plan-badge-vip';
      default:
        return 'plan-badge-basic';
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-badge-active';
      case 'canceled':
        return 'status-badge-canceled';
      case 'past_due':
        return 'status-badge-past-due';
      default:
        return 'status-badge-inactive';
    }
  };

  const formatAmount = (amount: string | number): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return (numAmount / 100).toFixed(2); // Convertir les centimes en euros
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatPlanPrice = (plan: Plan): string => {
    const amount = plan.price.toFixed(2).replace('.', ',');
    const intervalLabel = plan.interval === 'month' ? 'mois' : 'an';
    return `${amount}€/${intervalLabel}`;
  };

  const getPlanHeadline = (plan: Plan): string => {
    const priceLabel = formatPlanPrice(plan);
    switch (plan.id) {
      case 'basic':
        return `Découverte – ${priceLabel}`;
      case 'premium':
        return `Le plus populaire – ${priceLabel}`;
      case 'vip':
        return `Expérience totale – ${priceLabel}`;
      default:
        return `${plan.name} – ${priceLabel}`;
    }
  };

  const getPlanObjective = (plan: Plan): string => {
    switch (plan.id) {
      case 'basic':
        return 'Idéal pour tester la plateforme en douceur.';
      case 'premium':
        return 'Accédez à 50 vidéos par jour et à toutes les nouveautés HD/4K.';
      case 'vip':
        return 'Accès illimité, contenus exclusifs et priorité sur les futures sorties.';
      default:
        return 'Profitez de tous nos avantages exclusifs.';
    }
  };

  const isPlanRecommended = (planId: string): boolean => planId === 'premium';

  const isVipPlan = (planId: string): boolean => planId === 'vip';

  const trialPlanCandidates = ['premium_trial', 'premiumTrial', 'premium-essai'];
  const trialPlan = plans.find((plan) => trialPlanCandidates.includes(plan.id));
  const premiumPlan = plans.find((plan) => plan.id === 'premium');
  const defaultTrialTarget = trialPlan?.id || premiumPlan?.id || plans[0]?.id || '';
  const hasPremiumOrVip = mySubscriptions.some(
    (subscription) =>
      subscription.status === 'active' &&
      ['PREMIUM', 'VIP'].includes(subscription.plan?.toUpperCase() ?? '')
  );
  const trialEligibilityKnown = !isAuthenticated || subscriptionsLoaded;
  const shouldShowTrial = Boolean(
    trialEligibilityKnown &&
      defaultTrialTarget &&
      (trialPlan || premiumPlan) &&
      !hasPremiumOrVip &&
      !hasUsedTrial &&
      !localTrialUsed
  );
  const trialPriceLabel = trialPlan
    ? `${trialPlan.price.toFixed(2).replace('.', ',')}€`
    : '3,99€';

  if (loading) {
    return (
      <div className="subscriptions-container">
        <div className="loading">Chargement des plans d'abonnement...</div>
      </div>
    );
  }

  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '') || 'https://www.edenplaisir.com/subscriptions'

  return (
    <div className="subscriptions-container">
      <Helmet>
        <title>Plans d'abonnement - EdenPlaisir.com</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content="Découvrez nos plans d'abonnement premium pour accéder aux meilleures vidéos X HD de LetsJerkTV, RedTube, Eporner et Pornhub. Abonnements flexibles, paiement sécurisé, annulation à tout moment." />
        <meta name="keywords" content="abonnement vidéos X, plan premium vidéos, abonnement porno, abonnement EdenPlaisir, plan VIP vidéos, abonnement HD, abonnement sécurisé, plan mensuel vidéos, abonnement sans engagement, offres abonnement vidéos adultes" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Plans d'abonnement - EdenPlaisir.com" />
        <meta property="og:description" content="Découvrez nos plans d'abonnement premium pour accéder aux meilleures vidéos X HD. Abonnements flexibles, paiement sécurisé." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Plans d'abonnement - EdenPlaisir.com" />
        <meta name="twitter:description" content="Découvrez nos plans d'abonnement premium pour accéder aux meilleures vidéos X HD." />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Plans d'abonnement - EdenPlaisir.com",
          "description": "Découvrez nos plans d'abonnement premium pour accéder aux meilleures vidéos X HD de LetsJerkTV, RedTube, Eporner et Pornhub. Abonnements flexibles, paiement sécurisé, annulation à tout moment.",
          "url": "https://www.edenplaisir.com/subscriptions",
          "inLanguage": "fr",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Eden Plaisir",
            "url": "https://www.edenplaisir.com"
          },
          "about": {
            "@type": "Product",
            "name": "Abonnements Premium EdenPlaisir",
            "description": "Plans d'abonnement pour accéder à des vidéos X HD de qualité depuis LetsJerkTV, RedTube, Eporner et Pornhub",
            "brand": {
              "@type": "Organization",
              "name": "Eden Plaisir",
              "url": "https://www.edenplaisir.com"
            },
            "offers": plans.map((plan) => ({
              "@type": "Offer",
              "name": plan.name,
              "description": plan.features.join(", "),
              "price": plan.price,
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": plan.price,
                "priceCurrency": "EUR",
                "billingIncrement": plan.interval === "month" ? "P1M" : "P1Y",
                "unitCode": plan.interval === "month" ? "MON" : "ANN"
              }
            }))
          }
        })}
        </script>
      </Helmet>

      <SubscriptionModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        type="reminder"
        redirectContext={intent}
      />

      <div className={`subscriptions-hero ${hasHotIntent ? 'subscriptions-hero-hot' : ''}`}>
        <div className="subscriptions-hero-copy">
          {hasHotIntent ? (
            <>
              <p className="hero-eyebrow">Vous étiez sur le point de regarder une vidéo Premium 🔥</p>
              <h1>Rejoignez les membres EdenPlaisir</h1>
              <p className="hero-subtitle">Accédez à tout notre contenu en illimité, sans publicité, et profitez de la meilleure expérience HD/4K.</p>
            </>
          ) : (
            <>
              <h1>Plans d'Abonnement</h1>
              <p className="hero-subtitle">Choisissez l'offre qui vous convient et débloquez immédiatement nos contenus exclusifs.</p>
            </>
          )}
        </div>
        {hasHotIntent && (
          <div className="subscriptions-hero-preview">
            <div className="hero-preview-image">
              {intent.videoThumbnail ? (
                <img
                  src={intent.videoThumbnail}
                  alt={intent.videoTitle ? `Aperçu de ${intent.videoTitle}` : 'Aperçu de la vidéo Premium'}
                  loading="lazy"
                />
              ) : (
                <div className="hero-preview-placeholder">Aperçu Premium</div>
              )}
              <div className="hero-preview-overlay">
                <span>Cette vidéo est disponible avec l'abonnement Premium.</span>
                {intent.videoTitle && <strong>{intent.videoTitle}</strong>}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {shouldShowTrial && (
        <div className="trial-offer-card">
          <div className="trial-offer-content">
            <span className="trial-offer-badge">💎 Offre d'essai</span>
            <h2>Profitez d’un accès complet pendant 7 jours pour seulement <strong className="trial-offer-price">{trialPriceLabel}</strong> !</h2>
            <p>
              Découvrez l’intégralité de notre catalogue premium sans engagement.
              À l’issue de la période d’essai, votre abonnement sera automatiquement renouvelé en formule Premium à 14,99 €/mois, sauf résiliation avant la fin des 7 jours.
              Vous pouvez annuler à tout moment, simplement et sans frais.
            </p>
          </div>
          <button
            className="trial-offer-button"
            onClick={() => handleSubscribe(defaultTrialTarget)}
            disabled={processingPlan === defaultTrialTarget}
          >
            {processingPlan === defaultTrialTarget ? 'Chargement...' : `Activer l'essai 7 jours`}
          </button>
        </div>
      )}

      {/* Mes abonnements actifs */}
      {isAuthenticated && (mySubscriptions.length > 0 || loadingSubscriptions) && (
        <div className="my-subscriptions-section">
          <h2>Mes Abonnements</h2>
          {loadingSubscriptions ? (
            <div className="loading">Chargement de vos abonnements...</div>
          ) : (
            <div className="my-subscriptions-list">
            {mySubscriptions.map((subscription) => (
              <div key={subscription.id} className="my-subscription-card">
                <div className="subscription-info">
                  <div className="subscription-header">
                    <h3>{subscription.planName || subscription.plan}</h3>
                    <span className={`status-badge ${getStatusBadgeClass(subscription.status)}`}>
                      {subscription.status}
                    </span>
                  </div>
                  {subscription.description && (
                    <p className="subscription-description">{subscription.description}</p>
                  )}
                  <p className="subscription-price">
                    {formatAmount(subscription.amount)} {subscription.currency.toUpperCase()} / {subscription.interval === 'month' ? 'mois' : 'an'}
                  </p>
                  {subscription.currentPeriodEnd && (
                    <p className="subscription-period">
                      Expire le {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  )}
                </div>
                {(subscription.status === 'active' || subscription.status === 'trialing') && (
                  <button
                    className="cancel-button"
                    onClick={() => handleCancelSubscription(subscription)}
                    disabled={cancelingSubscription === Number(subscription.id)}
                  >
                    {cancelingSubscription === Number(subscription.id) ? 'Annulation...' : 'Résilier l\'abonnement'}
                  </button>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
      )}

      {/* Plans disponibles */}
      <div className="plans-grid" ref={planSectionRef}>
        {plans
          .filter((plan) => plan.id !== 'premium_trial')
          .map((plan) => {
          const cardClasses = ['plan-card', `plan-${plan.id}`];
          const recommended = isPlanRecommended(plan.id);
          const vip = isVipPlan(plan.id);

          if (recommended) {
            cardClasses.push('plan-card-recommended');
          }
          if (vip) {
            cardClasses.push('plan-card-vip');
          }

          const headline = getPlanHeadline(plan);
          const objective = getPlanObjective(plan);

          return (
            <div key={plan.id} className={cardClasses.join(' ')}>
              <div className="plan-card-header">
                <div className={`plan-badge ${getPlanBadgeClass(plan.id)}`}>
                  {plan.name}
                </div>
                {recommended && <span className="plan-recommended-badge">🔥 Recommandé</span>}
                {vip && <span className="plan-vip-highlight">Expérience totale</span>}
              </div>

              <p className="plan-headline">{headline}</p>
              <p className="plan-objective">{objective}</p>

              <div className="plan-price">
                <span className="price-amount">{plan.price.toFixed(2)}</span>
                <span className="price-currency">€</span>
                <span className="price-interval">/{plan.interval === 'month' ? 'mois' : 'an'}</span>
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className={`subscribe-button subscribe-button-${plan.id}`}
                onClick={() => handleSubscribe(plan.id)}
                disabled={processingPlan === plan.id}
              >
                {processingPlan === plan.id ? 'Chargement...' : 'S\'abonner maintenant'}
              </button>

              <ul className="plan-trust">
                <li>🔒 Paiement discret et sécurisé</li>
                <li>⚡ Accès immédiat après souscription</li>
                <li>❌ Annulez quand vous voulez</li>
              </ul>
            </div>
          );
        })}
      </div>

      <div className="subscriptions-footer">
        <p>Tous les abonnements sont renouvelés automatiquement. Vous pouvez annuler à tout moment.</p>
        <p>Paiement sécurisé par Stripe.</p>
      </div>

      <div className="subscriptions-sticky-cta">
        <button onClick={scrollToPlans}>Voir les plans</button>
      </div>
    </div>
  );
};

export default Subscriptions;

